import {
  emptyContact,
  newSnapshot,
  snapshotSchema,
  type Snapshot,
  type ContactDraft,
  type PreviewDraft,
} from "./model";
const key = "qotta.businessOnboarding.v1";
const lifetime = 7 * 24 * 60 * 60 * 1000;
export function restoreJourney(): Snapshot {
  try {
    const result = snapshotSchema.safeParse(JSON.parse(localStorage.getItem(key) ?? "null"));
    if (
      result.success &&
      Date.now() - result.data.updatedAt < lifetime
    ) {
      return {
        ...result.data,
        panel:
          result.data.panel === "analyzing" || result.data.panel === "ready"
            ? "interrupted"
            : result.data.panel === "contact"
              ? "journey"
              : result.data.panel,
      };
    }
  } catch {
    /* Storage is optional; continue in memory. */
  }
  return newSnapshot();
}
export function rememberJourney(state: Snapshot) {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* No browser persistence available. */
  }
}
// Contact drafts are short-lived, tab-scoped, and never written to localStorage.
export function restoreDrafts(id: string): { contact: ContactDraft; preview: PreviewDraft } {
  try {
    const value = JSON.parse(sessionStorage.getItem(key + ".draft." + id) ?? "null");
    if (value && Date.now() - value.at < 60 * 60 * 1000) {
      const clean = (fields: string[], source: Record<string, unknown>) =>
        Object.fromEntries(
          fields.map((field) => [
            field,
            typeof source?.[field] === "string" ? (source[field] as string).slice(0, 2048) : "",
          ]),
        );
      return {
        contact: clean(Object.keys(emptyContact), value.contact) as ContactDraft,
        preview: clean(["websiteUrl", "name", "phone"], value.preview) as PreviewDraft,
      };
    }
  } catch {
    /* Start with empty drafts. */
  }
  return { contact: { ...emptyContact }, preview: { websiteUrl: "", name: "", phone: "" } };
}
export function rememberDrafts(id: string, contact: ContactDraft, preview: PreviewDraft) {
  try {
    sessionStorage.setItem(
      key + ".draft." + id,
      JSON.stringify({ at: Date.now(), contact, preview }),
    );
  } catch {
    /* Retain in component memory. */
  }
}
export function clearDrafts(id: string) {
  try {
    sessionStorage.removeItem(key + ".draft." + id);
    sessionStorage.removeItem(key + ".proof." + id);
  } catch {
    /* Optional storage. */
  }
}

export function rememberProof(id: string, proof: ContactDraft | PreviewDraft) {
  try {
    sessionStorage.setItem(key + ".proof." + id, JSON.stringify({ at: Date.now(), proof }));
  } catch {
    /* Keep acknowledged details in memory. */
  }
}
export function restoreProof(id: string): ContactDraft | PreviewDraft | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(key + ".proof." + id) ?? "null");
    if (
      value &&
      Date.now() - value.at < 60 * 60 * 1000 &&
      typeof value.proof?.name === "string" &&
      typeof value.proof?.phone === "string"
    )
      return value.proof;
  } catch {
    /* No acknowledged draft. */
  }
  return null;
}

/** Explicit local restart. No server documents or authentication are changed. */
export function resetJourney(): Snapshot {
  try {
    const previous = snapshotSchema.safeParse(JSON.parse(localStorage.getItem(key) ?? "null"));
    if (previous.success) clearDrafts(previous.data.journeyId);
    localStorage.removeItem(key);
  } catch {
    /* Storage can be unavailable; a new in-memory journey is still allowed. */
  }
  const fresh = newSnapshot();
  rememberJourney(fresh);
  return fresh;
}

export const resetJourneyForAdmin = resetJourney;

/** Discard late work from an unmounted/replaced journey, including a reset in another tab. */
export function isCurrentJourney(id: string): boolean {
  try {
    const stored = JSON.parse(localStorage.getItem(key) ?? "null");
    return !stored?.journeyId || stored.journeyId === id;
  } catch {
    return true;
  }
}
